import React, {useEffect, useRef, useState} from 'react';
import styled from "styled-components";
import {create_UUID} from "../../shared/utils";
import Icon from "../Icon";
import {AnimatePresence, motion} from "framer-motion"
// RCE CSS
import 'react-chat-elements/dist/main.css'
// MessageBox component
import {Input, MessageBox} from 'react-chat-elements'
import Button from "../Button";
import ChatManager, {ChatMessage} from "../../shared/ChatManager";
import {useCountryLang} from "../../hooks/useCountryLang";
import {IChatMessage} from "../../interfaces/IChatMessage";
import {useMessages} from "../../hooks/useMessages";
import {useAtom} from "jotai";
import {nextSessionAtom} from "../../store/sessionAtom";
import {getTheme} from "../../shared/themeUtils";

const ChatContain = styled.div`
  position: fixed;
  bottom: ${getTheme("space.3")};
  right: ${getTheme("space.3")};
  z-index: 100;

  .toggle-btn {
    position: relative;
    border: none;
    background-color: ${getTheme("colors.darkBlue")};
    background-image: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 10px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all .3s;

    &:hover {
      transform: scale(1.1);
    }
  }

  .chat-body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    width: 400px;
    height: 600px;
    max-height: 60vh;
    border-radius: ${getTheme("space.2")};
    background: ${getTheme("colors.white")};
    position: absolute;
    right: 0;
    bottom: 80px;
    box-shadow: rgba(0, 0, 0, 0.24) 0px 16px 40px;

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background-color: ${getTheme("colors.darkBlue")};
      background-image: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      padding: ${getTheme("space.2")};
      width: 100%;
      top: 0;
      border-top-left-radius: ${getTheme("space.2")};
      border-top-right-radius: ${getTheme("space.2")};
      color: ${getTheme("colors.white")};
      font-weight: bold;
      text-align: center;
      .hello-span {
        display: flex;
        align-items: center;
      }
      .hello-icon {
        margin-left: ${getTheme("space.1")};
        max-width: 40px;
      }
      button {
        background: transparent;
        border: none;
      }
    }
    
    .scroll-contain {
      width: 100%;
      height: 100%;
      padding: ${getTheme("space.2")};
      padding-bottom: 0;
      overflow-y: scroll;
      button {
        right: 20px;
        bottom: 90px;
      }
    }

    .messages {
      
      .rce-container-mbox.message-entry {
        margin-bottom: ${getTheme("space.2")};
        .rce-mbox {
          padding: ${getTheme("space.2")};
          max-width: 80%;
          box-shadow: none;
          background: ${getTheme("colors.background")};
          border-radius: ${getTheme("space.1")};
          margin: 0;
        }
        .rce-mbox-right {
          background: #c1c4ff;
        }
        .rce-mbox-right-notch {
          display: none;
        }
        .rce-mbox-left-notch {
          display: none;
        }
      }
    }

    .input-area {
      width: 100%;
      padding: ${getTheme("space.2")};
      border-top: 1px solid ${getTheme("colors.border")};
      position: relative;
      padding-right: ${getTheme("space.4")};
      
      .submit-btn {
        position: absolute; 
        right: ${getTheme("space.2")};
        bottom: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
        .submit-icon {
          opacity: .6;
        }
      }
    }
  }

  @media (max-width: ${getTheme("breakpoints.0")}) {
    bottom: ${getTheme("space.2")};
    right: ${getTheme("space.2")};
    .chat-body {
      width: 100vw;
      height: 100%;
      max-height: 100vh;
      position: fixed;
      top: 0;
      bottom: 0;
      .header {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
  }
`

const debug = false;

const ChatWidget = () => {
  const countryLang = useCountryLang();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [message, setMessage] = useState("")
  const [chatManager, setChatManager] = useState<ChatManager>();
  const [sessionAtom] = useAtom(nextSessionAtom)
  const {session, status} = sessionAtom;
  const [showChatBox, setShowChatBox] = useState(false);
  const [sessionMessages, setSessionMessages] = useState([] as any)
  const [handlerName, setHandlerName] = useState("")
  const messagesQuery = useMessages()
  const messagesEndRef: any = useRef(null);

  const messageFromVisitorErrorCallback = () => {
    //TODO: Implement
  }

  const messageFromOperatorCallback = (chatMessage: ChatMessage) => {
    //Message received from customer advisor
    //chatMessage.message

    if (countryLang!=null) {
      const chatManager: ChatManager = ChatManager.getInstance(countryLang, session, status);
      //Session messages
      setSessionMessages(chatManager?.getChatMessagesFromSession());
      debug && console.log("sessionMessages: " + JSON.stringify(sessionMessages));
    }
  }

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };


  const handleTypeMessage = (e: any) => {
    setMessage(e.target.value)
  }


  const handleEnterSubmit = (e: any) => {
    if (e.keyCode == 13) {
      handleSubmitMessage(e.target.value)
    }
  }


  const handleSubmitMessage = async (text: string) => {
    // submit logic here
    if (text.length > 0) {
      debug && console.log("Customer message: " + text + " | chatManager: " + JSON.stringify(chatManager));
      setMessage("")
      await chatManager?.onMessageFromVisitor(text, window.origin)
      const res = chatManager?.getChatMessagesFromSession()
      setSessionMessages(res)
    }
  }

  useEffect(() => {
    if (!chatManager && countryLang && status !== "loading") {
      const chatManager: ChatManager = ChatManager.getInstance(countryLang, session, status);
      chatManager.setMessageFromOperatorCallback(messageFromOperatorCallback);
      chatManager.setMessageFromVisitorErrorCallback(messageFromVisitorErrorCallback);
      setChatManager(chatManager);
      setSessionMessages(chatManager?.getChatMessagesFromSession());
    }

    debug && console.log("showChatBox: " + showChatBox);
    if (!showChatBox && chatManager) {
      chatManager.shouldShowChat().then(res => {
        if (res) {
          debug && console.log("setShowChatBox true");
          setShowChatBox(true);
        }
      });
    }
  }, [chatManager, countryLang, status]);

  useEffect(() => {
    getHandlerName()
    scrollToBottom()
  }, [sessionMessages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  };

  const getHandlerName = () => {
    if (sessionMessages && !handlerName) {
      sessionMessages.map((message: IChatMessage) => {
        if (message.source == 0 && message.handlerUser) {
          setHandlerName(message.handlerUser)
        }
      })
    }
  }

  if (showChatBox) {
    return (
      <ChatContain>
        <button
          className="toggle-btn"
          onClick={toggleChat}
        >
          <AnimatePresence>
            {isChatOpen ?
              <motion.span initial={{rotate: -180}}
                           animate={{rotate: 0}}
                           exit={{rotate: -180}}>
                <Icon variant={"large"} color={"white"}>arrow-down-s-line</Icon>
              </motion.span> :
              <Icon variant={"large"} color={"white"}>chat-smile-2-line</Icon>
            }
          </AnimatePresence>
        </button>

        <AnimatePresence>
          {isChatOpen &&
            <motion.div className={`chat-body`}
                        key="modal"
                        initial={{right: '-400px', opacity: 0}}
                        animate={{right: 0, opacity: 1}}
                        exit={{right: '-400px', opacity: 0}}
            >
              <div className="header">
                {!handlerName &&
                  <span className={"hello-span"}>
                  {messagesQuery.data && messagesQuery.data.messages["chat-window-initial-title"]}
                    <img className="hello-icon" src="/icons/hand-hello.svg" alt="hello"/>
                </span>
                }
                {handlerName &&
                  <span className={"hello-span"}>{handlerName} <img className="hello-icon" src="/icons/hand-hello.svg"
                                                                    alt="hello"/></span>
                }
                <button
                  onClick={toggleChat}>
                  <Icon variant={"large"} color={"white"}>close-line</Icon>
                </button>
              </div>
              <div className={"scroll-contain"}>
                <div className="messages">
                  {sessionMessages && sessionMessages.map((message: IChatMessage) => {
                    // Base props for MessageBox
                    const messageBoxProps: any = {
                      position: message.source === 0 ? 'left' : 'right',
                      type: 'text',
                      text: message.message,
                      className: "message-entry",
                      date: new Date(message.messageTime),
                    };

                    // Conditionally add the 'status' prop
                    if (message.source !== 0) {
                      if (message.readTime != null) {
                        messageBoxProps.status = "read";
                      } else {
                        messageBoxProps.status = "sent";
                      }
                    }
                    return (
                      <div key={create_UUID()}>
                        {/*// @ts-ignore*/}
                        <MessageBox {...messageBoxProps} />
                      </div>
                    )
                  })}
                  <div ref={messagesEndRef}/>
                </div>
              </div>
              <div className="input-area">
                <Input multiline={false}
                       maxHeight={200}
                       placeholder={"Your message..."}
                       value={message}
                       onKeyDown={(e: any) => handleEnterSubmit(e)}
                       onChange={(e: any) => handleTypeMessage(e)}
                />
                <Button variant={"transparent"}
                        onClick={() => handleSubmitMessage(message)}
                        className={"submit-btn p-1"}>
                  <Icon color={"textBlue"} className={"submit-icon"} variant="small">
                    send-plane-fill
                  </Icon>
                </Button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </ChatContain>
    );
  }

  return (
    <></>
  )
};

export default ChatWidget;
